import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

import type {
  ContentBlock,
  ContentBlockType,
  ContentTypeMap,
  DisplayMessage,
  PendingToolCall,
  TextContentBlock,
} from "./types";

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/**
 * Truncate a string to `maxLen` characters, appending "…" if it was cut.
 * Defaults to 80 characters — matching the preview width used throughout
 * the content-block components.
 */
export function truncate(str: string, maxLen = 80): string {
  return str.length > maxLen ? str.slice(0, maxLen) + "..." : str;
}

// ---------------------------------------------------------------------------
// Content block helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when a streaming content array contains at least one block
 * with non-empty visible content.
 *
 * The Responses API / thinking models push blocks with empty string values
 * (e.g. `{ type: "thinking", thinking: "" }` or `{ type: "text", text: "" }`)
 * before the first real token arrives. Treating those as "has content" causes
 * skeleton UIs to exit prematurely and flash blank / invisible content.
 *
 * Rules:
 *  - `text` blocks  → visible only when `text.length > 0`
 *  - `thinking` blocks → visible only when `thinking.length > 0`
 *  - all other block types → visible as soon as they appear in the array
 *    (tool_use, media, shell_call, etc. are structural and always renderable)
 *
 * Use this anywhere you need to decide whether to show a streaming skeleton
 * vs. real content.
 */
export function hasVisibleStreamingContent(
  blocks: ContentBlock[] | null | undefined,
): boolean {
  if (!blocks || blocks.length === 0) {return false;}
  return blocks.some((b) => {
    if (b.type === "text")
      {return (b as { type: "text"; text: string }).text.length > 0;}
    if (b.type === "thinking")
      {return (b as { type: "thinking"; thinking: string }).thinking.length > 0;}
    return true;
  });
}

/** Extract all plain text from an array of content blocks. */
export function extractText(blocks: ContentBlock[]): string {
  return blocks
    .filter((b): b is TextContentBlock => b.type === "text")
    .map((b) => b.text)
    .join("");
}

/** Convert a plain string into a single-element content block array. */
export function textToBlocks(text: string): ContentBlock[] {
  return [{ type: "text", text }];
}

/** Filter content blocks by type with full type-narrowing. */
export function getBlocksByType<T extends ContentBlockType>(
  blocks: ContentBlock[],
  type: T,
): ContentTypeMap[T][] {
  return blocks.filter((b): b is ContentTypeMap[T] => b.type === type);
}

// ---------------------------------------------------------------------------
// Tool-call response helpers
// ---------------------------------------------------------------------------

/** Pretty-print a JSON arguments string; returns raw string on parse failure. */
export function formatArguments(args: string): string {
  try {
    return JSON.stringify(JSON.parse(args), null, 2);
  } catch {
    return args;
  }
}

/**
 * Inspect messages and extract pending tool calls from the last assistant message.
 * Returns an empty array when there are no pending tool calls.
 * Works with any message type that has `role`, `tool_calls` with `id` and `function`.
 */
export function detectPendingToolCalls(
  messages: Pick<DisplayMessage, "role" | "tool_calls">[],
): PendingToolCall[] {
  if (messages.length === 0) {return [];}

  const last = messages[messages.length - 1];
  if (last.role !== "assistant") {return [];}

  const toolCalls = last.tool_calls;
  if (!toolCalls || toolCalls.length === 0) {return [];}

  return toolCalls
    .filter((tc) => !!tc.id)
    .map((tc) => ({
      tool_call_id: tc.id,
      name: tc.function?.name ?? "tool",
      arguments: tc.function?.arguments ?? "{}",
    }));
}
